/* eslint-disable react/prop-types */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../../components/ui/dialog";
import { fmtDMY, fmtHM } from "../../../lib/instructorView";

const TYPE_LABEL = {
  instrucao: "Instrução",
  solo_supervisionado: "Solo supervisionado",
  largada_solo: "Largada solo",
  voo_solo: "Voo solo",
  viagem: "Viagem",
  treino: "Treino",
  pic: "PIC",
  dc: "DC",
  uniformizacao: "Uniformização",
  outro: "Outro",
};

/**
 * Registos da caderneta que sustentam um número do cartão do instrutor: o que conta, o que não conta e porquê,
 * e o total. Usado pelas 25 h de instrução (revalidação) e pelas horas dos últimos 12 meses.
 * Os dados vêm do servidor, por isso este ecrã não recalcula nada.
 */
export default function InstructionRecordsDialog({
  open, onClose, title, subtitle, records, totalMin, requiredMin, footnote, countLabel = "Conta", showNotCounted = true,
}) {
  const recs = records || [];
  const counted = recs.filter((r) => r.counted);
  const notCounted = recs.filter((r) => !r.counted);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="di-instruction-records">
        <DialogHeader>
          <DialogTitle className="text-white text-sm">{title}</DialogTitle>
          {subtitle && <DialogDescription className="text-[11px] text-slate-400">{subtitle}</DialogDescription>}
        </DialogHeader>

        <div className={`grid gap-2 text-center text-[12px] ${showNotCounted ? "grid-cols-2" : "grid-cols-1"}`}>
          <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-2" data-testid="di-records-counted">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">{countLabel}</div>
            <div className="font-mono text-base text-emerald-300">
              {fmtHM(totalMin ?? 0)}{requiredMin ? <span className="text-slate-400"> de {fmtHM(requiredMin)}</span> : null}
            </div>
            <div className="text-[10px] text-slate-500">{counted.length} registo(s)</div>
          </div>
          {showNotCounted && (
            <div className="rounded border border-slate-700 p-2" data-testid="di-records-not-counted">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Não conta</div>
              <div className="font-mono text-base text-slate-300">{notCounted.length} registo(s)</div>
              <div className="text-[10px] text-slate-500">o motivo está em cada linha</div>
            </div>
          )}
        </div>

        {recs.length === 0 ? (
          <div className="text-xs text-slate-500">Não há registos nesta janela.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-700">
                  <th className="py-1 pr-2">Data</th>
                  <th className="pr-2">Tipo</th>
                  <th className="pr-2">Aeronave</th>
                  <th className="pr-2">Registo</th>
                  <th className="pr-2 text-right">Minutos</th>
                  <th>Conta?</th>
                </tr>
              </thead>
              <tbody>
                {recs.map((r) => (
                  <tr key={r.id} className={`border-b border-slate-800 align-top ${r.counted ? "text-slate-100" : "text-slate-400"}`} data-testid="di-record-row">
                    <td className="py-1 pr-2 font-mono whitespace-nowrap">{fmtDMY(r.date)}</td>
                    <td className="pr-2">
                      {TYPE_LABEL[r.flight_type] || r.flight_type || "—"}
                      {r.role === "companion" && <span className="text-slate-500"> (acompanhante)</span>}
                    </td>
                    <td className="pr-2 font-mono">{r.aircraft || "—"}</td>
                    <td className="pr-2 font-mono">{[r.navigation_log_number, r.service_number].filter(Boolean).join(" / ") || "—"}</td>
                    <td className="pr-2 text-right font-mono">{fmtHM(r.minutes)}</td>
                    <td>
                      <span className={r.counted ? "text-emerald-300 font-semibold" : "text-amber-300 font-semibold"}>{r.counted ? "Sim" : "Não"}</span>
                      <div className="text-[10px] text-slate-500 leading-snug">{r.reason}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {footnote && <p className="text-[10px] text-slate-500">{footnote}</p>}
      </DialogContent>
    </Dialog>
  );
}
